import React, { useState, useEffect, useContext } from 'react';
import { getDashboard, getAnomalyDistribution, getVibrationHistory, getTemperatureHistory } from '../../services/bobAssistantService';
import AnomalyPredictionTable from './AnomalyPredictionTable';
import AnomalyDistributionChart from './AnomalyDistributionChart';
import VibrationChart from './VibrationChart';
import TemperatureChart from './TemperatureChart';
import OperatingTimeHeatmap from './OperatingTimeHeatmap';
import AlarmsList from './AlarmsList';
import FFTChart from './FFTChart';
import axiosInstance from '../../AxiosInstance';
import MyContext from '../../Context';
import Modal from '../modal/modal';
import {SelectInput, TextInput} from '../input/inputs';
import './BobAssistantDashboard.css';

const BobAssistantDashboard = ({ deviceId }) => {
    const [dashboardData, setDashboardData] = useState(null);
    const [anomalyDistribution, setAnomalyDistribution] = useState(null);
    const [vibrationData, setVibrationData] = useState([]);
    const [temperatureData, setTemperatureData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const [dashboardForm, setDashboardForm] = useState({});
    const {dashboards} = useContext(MyContext);

    const addToDashboard = () => {
        axiosInstance.post(`dashboard/${dashboardForm.dashboard_id}/add_widget/`, {
            name: dashboardForm.widget_name || 'BOB ASSISTANT',
            key: deviceId,
            type: 4, // BOB widget
        }).then(() => {
            window.location.reload();
        });
    };

    useEffect(() => {
        loadDashboardData();

        // Auto-refresh every 30 seconds
        const intervalId = setInterval(() => {
            loadDashboardData();
        }, 30000); // 30 seconds

        // Cleanup interval on unmount
        return () => clearInterval(intervalId);
    }, [deviceId]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [dashboard, distribution, vibration, temperature] = await Promise.all([
                getDashboard(deviceId),
                getAnomalyDistribution(deviceId, 30),
                getVibrationHistory(deviceId, 7),
                getTemperatureHistory(deviceId, 7)
            ]);

            setDashboardData(dashboard);
            setAnomalyDistribution(distribution);
            setVibrationData(vibration);
            setTemperatureData(temperature);
        } catch (err) {
            console.error('Error loading BOB ASSISTANT dashboard:', err);
            setError('Error loading dashboard. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="bob-dashboard-loading">
                <div className="spinner"></div>
                <p>Loading BOB ASSISTANT dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bob-dashboard-error">
                <p>{error}</p>
                <button onClick={loadDashboardData}>Retry</button>
            </div>
        );
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'start': return '#4CAF50';
            case 'stop': return '#F44336';
            default: return '#9E9E9E';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'start': return 'Operating';
            case 'stop': return 'Stopped';
            default: return 'Unknown';
        }
    };

    return (
        <div className="bob-dashboard">
            {/* Header con estado actual */}
            <div className="bob-dashboard-header">
                <div className="bob-status-section">
                    <div
                        className="bob-status-indicator"
                        style={{ backgroundColor: getStatusColor(dashboardData?.current_status) }}
                    />
                    <div className="bob-status-info">
                        <h3>BOB ASSISTANT</h3>
                        <span className="bob-status-text">
                            {getStatusText(dashboardData?.current_status)}
                        </span>
                    </div>
                </div>

                {dashboardData?.learning_status?.is_learning && (
                    <div className="bob-learning-badge">
                        <span className="learning-icon">📚</span>
                        <span>
                            Learning: {dashboardData.learning_status.progress}%
                            ({dashboardData.learning_status.days_remaining} days remaining)
                        </span>
                    </div>
                )}

                {dashboardData?.unacknowledged_alarms_count > 0 && (
                    <div className="bob-alarms-badge">
                        <span className="alarm-icon">⚠️</span>
                        <span>{dashboardData.unacknowledged_alarms_count} pending alarm(s)</span>
                    </div>
                )}

                <button
                    className="bob-add-dashboard-btn"
                    onClick={() => {
                        setDashboardForm({widget_name: 'BOB ASSISTANT'});
                        setShowDashboardModal(true);
                    }}
                >
                    Add to Dashboard
                </button>
            </div>

            <Modal name={'Add BOB to Dashboard'} show={showDashboardModal} setter={setShowDashboardModal} onClick={addToDashboard} buttonName={'Add'}>
                <div className={'mb-3'}>
                    <SelectInput options={dashboards} name={'Dashboard'} internalName={'dashboard_id'} form={dashboardForm} setForm={setDashboardForm}/>
                    <TextInput name={'Name'} internalName={'widget_name'} form={dashboardForm} setForm={setDashboardForm}/>
                </div>
            </Modal>

            {/* Tabs de navegación */}
            <div className="bob-tabs">
                <button
                    className={`bob-tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button
                    className={`bob-tab ${activeTab === 'alarms' ? 'active' : ''}`}
                    onClick={() => setActiveTab('alarms')}
                >
                    Alarms
                </button>
                <button
                    className={`bob-tab ${activeTab === 'heatmap' ? 'active' : ''}`}
                    onClick={() => setActiveTab('heatmap')}
                >
                    Heatmap
                </button>
                <button
                    className={`bob-tab ${activeTab === 'fft' ? 'active' : ''}`}
                    onClick={() => setActiveTab('fft')}
                >
                    FFT Analysis
                </button>
            </div>

            {/* Contenido según tab activo */}
            <div className="bob-tab-content">
                {activeTab === 'overview' && (
                    <div className="bob-overview">
                        {/* Métricas principales */}
                        <div className="bob-metrics-grid">
                            <div className="bob-metric-card">
                                <span className="metric-label">Anomaly Level</span>
                                <span
                                    className="metric-value"
                                    style={{
                                        color: dashboardData?.latest_report?.anomaly_level > 50
                                            ? '#F44336'
                                            : dashboardData?.latest_report?.anomaly_level > 25
                                                ? '#FF9800'
                                                : '#4CAF50'
                                    }}
                                >
                                    {dashboardData?.latest_report?.anomaly_level ?? '-'}%
                                </span>
                            </div>
                            <div className="bob-metric-card">
                                <span className="metric-label">Temperature</span>
                                <span className="metric-value">
                                    {dashboardData?.latest_report?.temperature?.toFixed(1) ?? '-'}°C
                                </span>
                            </div>
                            <div className="bob-metric-card">
                                <span className="metric-label">Vibration</span>
                                <span className="metric-value">
                                    {dashboardData?.latest_report?.vibration_level?.toFixed(2) ?? '-'}
                                </span>
                            </div>
                            <div className="bob-metric-card">
                                <span className="metric-label">Battery</span>
                                <span className="metric-value">
                                    {dashboardData?.latest_report?.battery_percentage ?? '-'}%
                                </span>
                            </div>
                        </div>

                        {/* Tabla de predicciones */}
                        {dashboardData?.latest_report?.predictions && (
                            <AnomalyPredictionTable
                                predictions={dashboardData.latest_report.predictions}
                            />
                        )}

                        {/* Gráfico de distribución de anomalías */}
                        {anomalyDistribution && (
                            <AnomalyDistributionChart data={anomalyDistribution.distribution} />
                        )}

                        {/* Gráficos de vibración y temperatura */}
                        <div className="bob-charts-row">
                            <VibrationChart data={vibrationData} />
                            <TemperatureChart data={temperatureData} />
                        </div>
                    </div>
                )}

                {activeTab === 'alarms' && (
                    <AlarmsList
                        deviceId={deviceId}
                        onAlarmAcknowledged={loadDashboardData}
                    />
                )}

                {activeTab === 'heatmap' && (
                    <OperatingTimeHeatmap deviceId={deviceId} />
                )}

                {activeTab === 'fft' && (
                    <FFTChart deviceId={deviceId} />
                )}
            </div>
        </div>
    );
};

export default BobAssistantDashboard;
